import React, { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select from "react-select";
import axios from 'axios';
import { toast } from 'react-toastify';

const EditMarginCapital = ({ show, onClose, editData, onUpdated }) => {
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const order_type = [
    { value: "0", label: "Subscription Order" },
    { value: "1", label: "Catering Order" },
    { value: "2", label: "Corporate Order" },
  ];

  const [selectedOption, setSelectedOption] = useState(null);
  const [marginCapital, setMarginCapital] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [marginLengthError, setMarginLengthError] = useState('');
  const [descLengthError, setDescLengthError] = useState('');

  useEffect(() => {
    if (editData) {
      setSelectedOption(order_type.find(opt => opt.value === String(editData.order_type)) || null);
      setMarginCapital(editData.margin_capital || '');
      setDescription(editData.description || '');
    } else {
      setSelectedOption(null);
      setMarginCapital('');
      setDescription('');
    }
  }, [editData, show]);

  useEffect(() => {
    if (!show) {
      setSelectedOption(null);
      setMarginCapital('');
      setDescription('');
    }
  }, [show]);

  // Capitalize first letter of description on input
  const handleDescriptionChange = (e) => {
    let val = e.target.value;
    if (val.length > 0) {
      val = val.charAt(0).toUpperCase() + val.slice(1);
    }
    setDescription(val);
    if (val.length <= 500) setDescLengthError('');
  };

  const handleUpdate = async () => {
    setErrors({});
    setMarginLengthError('');
    setDescLengthError('');

    // Validation checks
    if (!selectedOption) {
      setErrors({ order_type: 'Order Type is required' });
      return;
    }

    if (!marginCapital.trim()) {
      setErrors(prev => ({ ...prev, marginCapital: 'Margin Capital is required' }));
      return;
    }

    // marginCapital must be number 1-100
    const marginCapitalNumber = Number(marginCapital);
    if (isNaN(marginCapitalNumber) || marginCapitalNumber < 1 || marginCapitalNumber > 100) {
      setErrors(prev => ({ ...prev, marginCapital: 'Margin Capital must be a number between 1 and 100' }));
      return;
    }

    if (marginCapital.length > 50) {
      setMarginLengthError('Limit reached (max 50 characters)');
      return;
    }

    if (description.length > 500) {
      setDescLengthError('Limit reached (max 500 characters)');
      return;
    }

    const user_id = parseInt(localStorage.getItem("user_id"), 10);
    if (!user_id) {
      toast.error("User ID not found.");
      return;
    }

    const payload = {
      order_type: selectedOption.value,
      margin_capital: marginCapital.trim(),
      description: description.trim(),
      user_id,
    };

    try {
      await axios.put(`${baseURL}/margincapital/update_margin/${editData.sno}`, payload);
      toast.success("Margin Capital updated successfully!");
      onClose();
      if (onUpdated) onUpdated();
    } catch (error) {
      if (error.response && error.response.status === 422) {
        console.error('Validation errors:', error.response.data.errors);
        toast.error("Validation failed: " + JSON.stringify(error.response.data.errors));
      } else {
        toast.error("Update failed.");
        console.error("Update error:", error);
      }
    }
  };

  const handleCancel = () => {
    setSelectedOption(null);
    setMarginCapital('');
    setDescription('');
    onClose();
  };

  return (
    <Modal show={show} size="md" onHide={handleCancel}>
      <div className="modal-content">
        <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
          <h3 className="modal-title text-white">Update Margin Capital</h3>
          <Button variant="" type="button" className="close px-6 py-2" onClick={handleCancel}>
            <span className="text-white">×</span>
          </Button>
        </div>
        <div className="modal-body">
          <div className="row">
            <div className="col-lg-12 mb-2">
              <label className="text-black font-w500 fs-7">
                Order Type<span className="required">*</span>
              </label>
              <Select
                placeholder="Select Order Type"
                value={selectedOption}
                onChange={setSelectedOption}
                options={order_type}
                className={errors.order_type ? 'is-invalid' : ''}
              />
              {errors.order_type && <div className="text-danger">{errors.order_type}</div>}
            </div>
            <div className="col-lg-12 mb-2">
              <label className="text-black font-w500 fs-7">
                Margin Capital (in %)<span className="required">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.marginCapital ? 'is-invalid' : ''}`}
                placeholder="Enter Margin Capital"
                value={marginCapital}
                onChange={(e) => {
                  setMarginCapital(e.target.value);
                  if (e.target.value.length <= 50) setMarginLengthError('');
                }}
              />
              {errors.marginCapital && <div className="invalid-feedback d-block">{errors.marginCapital}</div>}
              {marginLengthError && <span className="text-danger">{marginLengthError}</span>}
            </div>
            <div className="col-lg-12 mb-2">
              <label className="text-black font-w500 fs-7">
                Description<span className=""></span>
              </label>
              <textarea
                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                placeholder="Enter Description"
                value={description}
                onChange={handleDescriptionChange}
                rows={4}
              />
              {errors.description && <div className="text-danger">{errors.description}</div>}
              {descLengthError && <span className="text-danger">{descLengthError}</span>}
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-end gap-3 mt-4 mb-4">
            <Button variant="primary" size="sm" onClick={handleUpdate}>
              Update Margin Capital <i className="fas fa-angle-double-right ms-2"></i>
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditMarginCapital;
